import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfileContext } from '../../../context/userProfileContext';
import InputWrapper from '../../../components/inputWrapper/InputWrapper';
import { User } from '../../../interfaces/User';
import { camelCaseToText } from '../../../utils/camelCaseToText';
import { Pension } from '../../../interfaces/Pension';
import Modules from '../../../utils/modules';
import { TaxReturn } from '../../../interfaces/taxReturn';
import { Mortgage } from '../../../interfaces/Mortgage';
import { APICore } from '../../../utils/api/apiCore';
import { displayNumericInputFieldsValue } from '../../../utils/displayNumericValues';
import { DebounceInput } from 'react-debounce-input';
import Select from 'react-select';
import { useEnumsContext } from '../../../context/EnumsContext';
import { number } from 'yup';
import { formatDate } from '../../../utils/formatDate';
import StatusHistory from '../../../components/StatusHistory';
import { getMortgageStatusTrackers } from '../../../utils/api/mortgage';

const Details = () => {
    const enums = useEnumsContext();

    const loggedInUser = new APICore().getLoggedInUser()?.user;
    const canUserEdit = loggedInUser && (loggedInUser?.roles.includes('agent') || loggedInUser?.roles.includes('operator') || loggedInUser?.isAdmin);

    const { profileContextData, onEditRound, users, allUsers, getUserById } = useContext(UserProfileContext);
    const { client } = profileContextData;
    const [mortgage, setMortgage] = useState<Mortgage>(profileContextData.currentRound as Mortgage);

    const selectedModule = profileContextData.selectedModule;
    const allowedStatusesOptions = selectedModule && Object.values(selectedModule.allowedStatuses);
    const selectedModuleName = selectedModule?.data.moduleName;
    const repType = selectedModuleName === Modules.pension ? 'agent' : 'rep';

    const [repAgents, setRepAgents] = useState<User[]>(
        users.filter((rep: User) => rep.roles.includes('agent') && rep.agentModules?.includes('mortgage')),
    );
    const [repOperators, setRepOperators] = useState<User[]>(
        users.filter((rep: User) => rep.roles.includes('operator') && rep.agentModules?.includes('mortgage')),
    );

    useEffect(() => {
        setRepAgents(users.filter((rep: User) => rep.roles.includes('agent') && rep.agentModules?.includes('mortgage')));
        setRepOperators(users.filter((rep: User) => rep.roles.includes('operator') && rep.agentModules?.includes('mortgage')));
    }, [users]);

    const hasOperator = (object: any): object is Pension | TaxReturn | Mortgage => {
        return 'operator' in object;
    };

    const callToOnEditRound = (data: any) => {
        if (mortgage) {
            onEditRound(data, selectedModuleName, mortgage._id, () => {
                setMortgage({ ...mortgage, ...data });
            });
        }
    };

    const onFieldsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        onEditRound({ [name]: value }, selectedModuleName, mortgage._id, () => setMortgage({ ...mortgage, [name]: value }));
    };

    const getBankOption = (bankNumber: number) => {
        return {
            label: bankNumber ? `${enums?.Banks?.find((bank: any) => bank.number == bankNumber)?.name} (${bankNumber})` : '',
            value: bankNumber || null,
        };
    };

    return (
        <div className="detailed-tab">
            <div className="select-area">
                {client && mortgage ? (
                    <>
                        {allowedStatusesOptions && allowedStatusesOptions.length > 0 ? (
                            <InputWrapper status={{ text: '' }} label="Status" wrapperClass="input-field select-field">
                                <Select
                                    options={allowedStatusesOptions.map((option: any, index: number) => ({
                                        label: camelCaseToText(option),
                                        value: option,
                                    }))}
                                    onChange={(item: any) => onEditRound({ status: item.value }, selectedModuleName, mortgage._id)}
                                    value={{ label: camelCaseToText(mortgage.status), value: mortgage.status }}
                                    className="d-flex flex-wrap"
                                    styles={{
                                        menu: (base) => ({
                                            ...base,
                                            width: '100%',
                                            // minWidth: '300px',
                                        }),
                                        control: (base) => ({
                                            ...base,
                                            width: '100%',
                                        }),
                                    }}
                                />
                            </InputWrapper>
                        ) : null}

                        {repAgents && repAgents.length > 0 ? (
                            <InputWrapper status={{ text: '' }} label={'Agent / Rep'} wrapperClass="input-field select-field">
                                <Select
                                    options={repAgents.map((option: any, index: number) => ({
                                        label: option.fullName,
                                        value: option._id,
                                    }))}
                                    onChange={(item: any) => onEditRound({ [repType]: item.value }, selectedModuleName, mortgage._id)}
                                    value={{
                                        label: repAgents.find((rep: any) => rep._id === mortgage.rep)?.fullName,
                                        value: mortgage.rep,
                                    }}
                                    className="d-flex flex-wrap"
                                    styles={{
                                        menu: (base) => ({
                                            ...base,
                                            width: '100%',
                                            // minWidth: '300px',
                                        }),
                                        control: (base) => ({
                                            ...base,
                                            width: '100%',
                                        }),
                                    }}
                                />
                            </InputWrapper>
                        ) : (
                            <div className="pension-status">No {repType} found.</div>
                        )}

                        <InputWrapper status={{ text: '' }} label="Pre-approval Banks" wrapperClass="input-field select-field">
                            <Select
                                isMulti
                                options={enums?.Banks?.map((bank: any) => getBankOption(bank.number))}
                                onChange={(items: any) =>
                                    onEditRound({ preApprovalBanks: items.map((item: any) => Number(item.value)) }, selectedModuleName, mortgage._id)
                                }
                                value={mortgage?.preApprovalBanks?.map((bankNumber: number) => getBankOption(bankNumber))}
                                className="d-flex flex-wrap"
                                styles={{
                                    menu: (base) => ({
                                        ...base,
                                        width: '100%',
                                        // minWidth: '300px',
                                    }),
                                    control: (base) => ({
                                        ...base,
                                        width: '100%',
                                    }),
                                }}
                            />
                        </InputWrapper>

                        <InputWrapper status={{ text: '' }} label="Selected Bank" wrapperClass="input-field select-field">
                            <Select
                                options={enums?.Banks?.map((bank: any) => getBankOption(bank.number))}
                                onChange={(item: any) => onEditRound({ selectedBank: Number(item.value) }, selectedModuleName, mortgage._id)}
                                value={getBankOption(mortgage?.selectedBank)}
                                className="d-flex flex-wrap"
                                styles={{
                                    menu: (base) => ({
                                        ...base,
                                        width: '100%',
                                        // minWidth: '300px',
                                    }),
                                    control: (base) => ({
                                        ...base,
                                        width: '100%',
                                    }),
                                }}
                            />
                        </InputWrapper>

                        {/* preApprovalBanks: [
            {
                type: Number,
                enum: [Banks.map(bank => bank.number)]
            }
        ],
        selectedBank: {
            type: Number,
            enum: [Banks.map(bank => bank.number)]
        }, */}
                        <InputWrapper status={{ text: '' }} label="Potential Saving Amount" wrapperClass="input-field input-field">
                            <DebounceInput
                                value={displayNumericInputFieldsValue(mortgage.potentialSavingAmount)}
                                className="form-control"
                                name="potentialSavingAmount"
                                type="number"
                                min={0}
                                max={100}
                                debounceTimeout={1500}
                                onChange={onFieldsChange}
                                disabled={!canUserEdit}
                            />
                        </InputWrapper>
                        <InputWrapper status={{ text: '' }} label="Signing Link" wrapperClass="input-field input-field">
                            <DebounceInput
                                value={mortgage.signingLink}
                                className="form-control"
                                debounceTimeout={1500}
                                name="signingLink"
                                onChange={onFieldsChange}
                                disabled={!canUserEdit}
                            />
                        </InputWrapper>
                        <div>
                            <b>Agent meeting date: </b>
                            <span>{mortgage.agentMeetingTime ? formatDate(mortgage.agentMeetingTime) : 'Not scheduled'}</span>
                        </div>
                        <div>
                            <b>Service meeting date: </b>
                            <span>
                                {mortgage.serviceMeeting?.eventStartTime ? (
                                    <>
                                        {formatDate(mortgage.serviceMeeting!.eventStartTime)}
                                        {mortgage.serviceMeeting.repId && (
                                            <span>
                                                {' '}
                                                with <b>{getUserById(mortgage.serviceMeeting.repId)?.fullName || 'Unknown'}</b>
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    'Not scheduled'
                                )}
                            </span>
                        </div>
                    </>
                ) : (
                    <div>Sorry, no data yet.</div>
                )}
                {client && mortgage && (
                    <StatusHistory fetchStatusTrackers={getMortgageStatusTrackers} moduleId={mortgage._id} moduleName="mortgage" />
                )}
            </div>
        </div>
    );
};

export default Details;
